/**
 * TaskPhaseTrail - Trilha de fases (estilo Bitrix) do modal de Tarefa
 *
 * Reaproveita o componente compartilhado `PhaseTrail` (mesmo visual do modal de
 * Negócio) e vale para os três módulos que compartilham o modal de tarefas:
 * Tarefas (normal), Retenção (retention) e Jurídico (legal).
 *
 * Regras aplicadas aqui (camada de negócio da trilha):
 *   - lista apenas as fases da CATEGORIA da tarefa, no módulo correspondente;
 *   - clique move a tarefa via RPC `move_task_phase` (validação server-side);
 *   - fase bloqueada quando a tarefa está fechada (concluída/cancelada) ou
 *     quando o usuário não tem `can_change_phase` na fase de origem/destino;
 *   - fase final (`is_final`) não move direto: delega ao fluxo de conclusão
 *     existente (dialog de concluir/cancelar), preservando motivo/observação.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-03 v1.0.0 - Criação inicial.
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { PhaseTrail } from '@/components/shared/PhaseTrail';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger condicional centralizado do projeto
const logger = createLogger('TaskPhaseTrail');

interface TaskPhaseTrailProps {
  taskId: string;
  /** Módulo da tarefa: normal | retention | legal */
  taskType: 'normal' | 'retention' | 'legal';
  /** Categoria da tarefa (escopo das fases exibidas) */
  categoryId: string | null | undefined;
  /** Fase atual */
  currentPhaseId: string | null | undefined;
  /** Tarefa concluída/cancelada — trilha somente leitura */
  isClosed?: boolean;
  /** Usuário logado é o responsável (contexto das permissões por fase) */
  isResponsible?: boolean;
  /** Chamado quando a fase destino é final (delegado ao dialog de conclusão) */
  onSelectFinalPhase?: (phaseId: string) => void;
}

export function TaskPhaseTrail({
  taskId,
  taskType,
  categoryId,
  currentPhaseId,
  isClosed = false,
  isResponsible = false,
  onSelectFinalPhase,
}: TaskPhaseTrailProps) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Fases da categoria da tarefa, já escopadas pelo módulo
  const { phases, isLoading } = useTaskPhases(categoryId ?? undefined, taskType);

  // Permissões granulares por fase (mesma fonte usada pelo modal)
  const { canDoOnPhase } = useTaskPhasePermissions(categoryId ?? undefined);

  // Movimentação em andamento (evita cliques duplicados)
  const [isMoving, setIsMoving] = React.useState(false);

  // Mapa auxiliar para consultar metadados da fase pelo id
  const mapaFases = React.useMemo(
    () => new Map((phases ?? []).map((f) => [f.id, f])),
    [phases],
  );

  /** Converte as fases da tarefa para o contrato genérico da trilha */
  const itens = React.useMemo(
    () =>
      (phases ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        color: f.color ?? null,
        sortOrder: f.sort_order,
      })),
    [phases],
  );

  /** Pode mover PARA a fase informada? Exige permissão na origem e no destino */
  const canMoveToPhase = React.useCallback(
    (phaseId: string) => {
      if (isClosed || isMoving) return false;
      const origemOk = canDoOnPhase(currentPhaseId, 'can_change_phase', isResponsible);
      const destinoOk = canDoOnPhase(phaseId, 'can_change_phase', isResponsible);
      return origemOk && destinoOk;
    },
    [isClosed, isMoving, canDoOnPhase, currentPhaseId, isResponsible],
  );

  /** Mensagem exibida no tooltip quando a fase não é clicável */
  const blockedReason = React.useCallback(
    (phaseId: string) => {
      if (phaseId === currentPhaseId) return null;
      if (isClosed) return 'Registro fechado: não é possível alterar a fase.';
      if (isMoving) return 'Alteração de fase em andamento…';
      if (!canDoOnPhase(currentPhaseId, 'can_change_phase', isResponsible)) {
        return 'Você não tem permissão para mover a partir da fase atual.';
      }
      if (!canDoOnPhase(phaseId, 'can_change_phase', isResponsible)) {
        return 'Você não tem permissão para mover para esta fase.';
      }
      return null;
    },
    [currentPhaseId, isClosed, isMoving, canDoOnPhase, isResponsible],
  );

  /** Clique na fase: fase final delega ao fluxo de conclusão; demais movem direto */
  const handleSelectPhase = React.useCallback(
    async (phaseId: string) => {
      const fase = mapaFases.get(phaseId);

      // Fase final exige o fluxo de conclusão (motivo/observação)
      if (fase?.is_final && onSelectFinalPhase) {
        onSelectFinalPhase(phaseId);
        return;
      }

      setIsMoving(true);
      try {
        const { error } = await supabase.rpc('move_task_phase', {
          p_task_id: taskId,
          p_new_phase_id: phaseId,
          p_note: null,
          p_tenant_hint: currentTenant?.id || null,
        } as any);
        if (error) throw error;

        toast.success('Fase alterada com sucesso');

        // Mesmas invalidações usadas pelo modal "Alterar Fase"
        queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
        queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
        queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
        queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
        queryClient.invalidateQueries({ queryKey: ['task-activity-log', taskId] });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro desconhecido';
        logger.error('Erro ao alterar fase pela trilha', message);
        toast.error(`Erro ao alterar fase: ${message}`);
      } finally {
        setIsMoving(false);
      }
    },
    [mapaFases, onSelectFinalPhase, taskId, currentTenant?.id, queryClient],
  );

  // Sem categoria/fases não há trilha para exibir
  if (isLoading || itens.length === 0) return null;

  return (
    <PhaseTrail
      phases={itens}
      currentPhaseId={currentPhaseId}
      canMoveToPhase={canMoveToPhase}
      blockedReason={blockedReason}
      onSelectPhase={handleSelectPhase}
      readOnly={isClosed || isMoving}
    />
  );
}
